import React from 'react';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash/string';

import { i18n as I18n } from '../../../utils/i18n';

// Components
import InstructionalFormActions from '../../instructionalForm/actions';
import Textbox from '../../fields/textbox';
import Checkbox from '../../fields/checkbox';
import PasswordWithStrengthIndicator from '../../fields/passwordWithStrengthIndicator';
import InputError from '../../fields/inputError';

import { useUserDetailsFormHook } from '../../../hooks/useUserDetailsFormHook';

const UserDetails = ({ fields, handleFieldChange, handleSubmitCallback }) => {
  const {
    errors,
    handleChange,
    handleSubmit,
    handlePasswordChange,
    marketingOptions,
    isValid,
  } = useUserDetailsFormHook({
    fields,
    handleFieldChange,
    handleSubmitCallback,
    validateEmailUniqueness: true,
  });

  const renderTextboxField = name => (
    <Textbox
      label={I18n.t(`registration.user_details.fields.${name}.label`)}
      name={camelCase(name)}
      value={fields[camelCase(name)]}
      placeholder={I18n.t(`registration.user_details.fields.${name}.label`)}
      error={!!errors[camelCase(name)]}
      errorMsg={errors[camelCase(name)]}
      onChange={handleChange}
    />
  );

  return (
    <form
      className="instructional-form__form"
      onSubmit={handleSubmit}
      data-testid="userDetailsForm"
      data-cy="userDetailsForm"
    >
      <h2 data-cy="registrationHeader">
        {I18n.t('registration.user_details.title')}
      </h2>
      <div className="field-multicol">
        {renderTextboxField('first_name')}
        {renderTextboxField('last_name')}
      </div>
      <div className="field-multicol">
        {renderTextboxField('phone_number')}
        {renderTextboxField('mobile_number')}
      </div>
      <div className="field-multicol">
        {renderTextboxField('email')}
        {renderTextboxField('confirm_email')}
      </div>
      <div className="field-multicol">
        <div className="field">
          <PasswordWithStrengthIndicator
            value={fields.password}
            onChange={handlePasswordChange}
          />
        </div>
        <Textbox
          label={I18n.t(
            'registration.user_details.fields.confirm_password.label'
          )}
          name="confirmPassword"
          type="password"
          value={fields.confirmPassword}
          placeholder={I18n.t(
            'registration.user_details.fields.confirm_password.label'
          )}
          error={!!errors.confirmPassword}
          errorMsg={errors.confirmPassword}
          onChange={handleChange}
        />
      </div>

      {!!errors.password && (
        <p className="field password-note-field">
          <InputError errorMsg={errors.password} />
        </p>
      )}

      {marketingOptions.length > 0 && (
        <>
          <p>{I18n.t('registration.user_details.marketing_options')}</p>
          <div className="instructional-form__checkboxes">
            {marketingOptions.map(option => (
              <div key={`marketingOptions${option.id}`}>
                <Checkbox
                  label={I18n.t('registration.user_details.contact_by', {
                    name: option.name,
                  })}
                  name="marketingOptions[]"
                  id={`marketingOptions${option.id}`}
                  checked={
                    fields.marketingOptions.indexOf(option.id.toString()) !== -1
                  }
                  value={option.id}
                  onChange={handleChange}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <InstructionalFormActions isValid={isValid} showBackBtn={false} />
    </form>
  );
};

UserDetails.propTypes = {
  fields: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleSubmitCallback: PropTypes.func,
};

UserDetails.defaultProps = {
  handleSubmitCallback: () => null,
};

export default UserDetails;
